import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ThemeContext from '../../ThemeContext';
import { updateTheme } from '../../services/themeApi';

// Default theme
const defaultTheme = {
  name: 'default',
  colors: {
    primary: '#0ea5e9',
    primaryLight: '#38bdf8',
    primaryDark: '#0284c7',
    primaryRgb: '14, 165, 233',
    secondary: '#06b6d4',
    secondaryLight: '#22d3ee',
    secondaryDark: '#0e7490',
    secondaryRgb: '6, 182, 212',
    success: '#198754',
    successLight: '#75b798',
    successDark: '#146c43',
    successRgb: '25, 135, 84',
    warning: '#ffc107',
    warningLight: '#ffda6a',
    warningDark: '#cc9a06',
    warningRgb: '255, 193, 7',
    danger: '#dc3545',
    dangerLight: '#ea868f',
    dangerDark: '#b02a37',
    dangerRgb: '220, 53, 69',
    errorBorder: '#dc3545',
    errorText: '#dc3545',
    errorBg: '#f8d7da',
    info: '#0dcaf0',
    infoLight: '#6edff6',
    infoDark: '#0aa2c0',
    infoRgb: '13, 202, 240',
    bgPrimary: '#ffffff',
    bgSecondary: '#f8f9fa',
    bgTertiary: '#e9ecef',
    bgDark: '#212529',
    textPrimary: '#212529',
    textSecondary: '#6c757d',
    textMuted: '#adb5bd',
    textWhite: '#ffffff',
    borderColor: '#dee2e6',
    borderLight: '#f8f9fa',
    borderDark: '#adb5bd',
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica Neue", Arial, sans-serif',
    titleFontSize: 24, // px
    bodyFontSize: 14,  // px
  },
};

// RAW font names you gave
const rawFontNames = [
  'Agency FB',
  'Aharoni',
  'Alameda Std (some regions)',
  'Aldhabi',
  'Algerian',
  'Aloise',
  'Alphabet Std',
  'Andalé Mono',
  'Angsana New',
  'AngsanaUPC',
  'Aparajita',
  'Arabic Typesetting',
  'Arial',
  'Arial Black',
  'Arial Narrow',
  'Arial Rounded MT Bold',
  'Arial Unicode MS',
  'Arnoldboecklin',
  'Artifakt Element',
  'Bahnschrift',
  'Baskerville Old Face',
  'Batang / BatangChe',
  'Bauhaus 93',
  'Bell MT',
  'Berlin Sans FB',
  'Bernard MT Condensed',
  'Binner Std',
  'Blackadder ITC',
  'Bodoni MT',
  'Book Antiqua',
  'Bookman Old Style',
  'Bookshelf Symbol 7',
  'Bradley Hand ITC',
  'Braggadocio',
  'Britanic Bold',
  'Browallia New',
  'BrowalliaUPC',
  'Brush Script MT',
  'Calibri',
  'Calibri Light',
  'Californian FB',
  'Calisto MT',
  'Cambria',
  'Cambria Math',
  'Candara',
  'Castellar',
  'Centaur',
  'Century',
  'Century Gothic',
  'Chiller',
  'Colonna MT',
  'Comic Sans MS',
  'Consolas',
  'Constantia',
  'Cooper Black',
  'Copperplate Gothic',
  'Corbel',
  'Cordia New',
  'CordiaUPC',
  'Courier New',
  'Curlz MT',
  'DaunPenh',
  'David',
  'DengXian',
  'DFKai-SB',
  'DilleniaUPC',
  'DokChampa',
  'Dotum / DotumChe',
  'Ebrima',
  'Edwardian Script ITC',
  'Elephant',
  'Engravers MT',
  'Eras Bold ITC',
  'Estrangelo Edessa',
  'EucrosiaUPC',
  'Euphemia',
  'FangSong',
  'Felix Titling',
  'Footlight MT Light',
  'Forte',
  'Franklin Gothic',
  'Franklin Gothic Medium',
  'Freestyle Script',
  'French Script MT',
  'Gabriola',
  'Gadugi',
  'Garamond',
  'Gautami',
  'Georgia',
  'Gigi',
  'Gill Sans',
  'Gill Sans MT',
  'Gill Sans Nova',
  'Globe Std',
  'Gloucester MT Extra Condensed',
  'Goudy Old Style',
  'Goudy Stout',
  'Gulim / GulimChe',
  'Gungsuh / GungsuhChe',
  'Haettenschweiler',
  'Harlow Solid Italic',
  'Harrington',
  'High Tower Text',
  'HoloLens MDL2 Assets',
  'Impact',
  'Imprint MT Shadow',
  'Inform Std',
  'IrisUPC',
  'Iskoola Pota',
  'JasmineUPC',
  'Javanese Text',
  'Jokerman',
  'Juice ITC',
  'KaiTi',
  'Kalinga',
  'Kartika',
  'Khmer UI',
  'KodchiangUPC',
  'Kokila',
  'Kristen ITC',
  'Krungthep',
  'Kuzurian',
  'Lao UI',
  'Latha',
  'Leelawadee',
  'Leelawadee UI',
  'LilyUPC',
  'Lucida Bright',
  'Lucida Calligraphy',
  'Lucida Console',
  'Lucida Fax',
  'Lucida Handwriting',
  'Lucida Sans',
  'Lucida Sans Typewriter',
  'Lucida Sans Unicode',
  'Magneto',
  'Mahajanapada',
  'Maiandra GD',
  'Malgun Gothic',
  'Mangal',
  'Marlett',
  'Matura MT Script Capitals',
  'Meiryo',
  'Meiryo UI',
  'Microsoft Himalaya',
  'Microsoft JhengHei',
  'Microsoft New Tai Lue',
  'Microsoft PhagsPa',
  'Microsoft Sans Serif neuen',
  'Microsoft Tai Le',
  'Microsoft Yi Baiti',
  'MingLiU / MingLiU-ExtB',
  'MingLiU_HKSCS / MingLiU_HKSCS-ExtB',
  'Miriam',
  'Miriam Fixed',
  'Mistral',
  'Modern No. 20',
  'Mongol Baiti',
  'Monotype Corsiva',
  'MoolBoran',
  'MS Gothic',
  'MS Mincho',
  'MS PGothic',
  'MS PMincho',
  'MT Extra',
  'Muktamalar',
  'MV Boli',
  'Myanmar Text',
  'Narkisim',
  'News Gothic MT',
  'Niagara Engraved',
  'Niagara Solid',
  'Nirmala UI',
  'Noto Sans CJK',
  'Nyakumi',
  'OCR A Extended',
  'Old English Text MT',
  'Onyx',
  'Palace Script MT',
  'Palatino Linotype',
  'Papyrus',
  'Parchment',
  'Perpetua',
  'Perpetua Titling MT',
  'Playbill',
  'Poor Richard',
  'Pristina',
  'Raavi',
  'Rage Italic',
  'Ravie',
  'Rockwell',
  'Rockwell Extra Bold',
  'Rod',
  'Sagoe UI',
  'Sagoe Script',
  'Sagoe Print',
  'Script MT Bold',
  'Segoe UI Emoji',
  'Segoe UI Symbol',
  'Shonar Bangla',
  'Showcard Gothic',
  'Shruti',
  'SimHei',
  'SimSun',
  'SimSun-ExtB',
  'Sitka',
  'Snap ITC',
  'Staccato222 BT',
  'Stencil',
  'Sylfaen',
  'Symbol',
  'Tahoma',
  'TeamViewer15',
  'Tempus Sans ITC',
  'Times New Roman',
  'Trajan Pro',
  'Trebuchet MS',
  'Tunga',
  'Tw Cen MT',
  'Urdu Typesetting',
  'Utsaah',
  'Vani',
  'Verdana',
  'Vijaya',
  'Viner Hand ITC',
  'Vivaldi',
  'Vladimir Script',
  'Vrinda',
  'Webdings',
  'Westminster',
  'Wide Latin',
  'Wingdings',
  'Wingdings 2',
  'Wingdings 3',
  'Yu Gothic',
  'Yu Mincho',
];

// Build fontOptions with proper CSS font-family stacks
const fontOptions = [
  {
    label: 'System Default',
    value: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  ...rawFontNames.map((name) => ({
    label: name,
    value: `"${name}", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`,
  })),
];

const ThemePage = () => {
  const { theme = defaultTheme, setTheme, isThemeLoaded } = useContext(ThemeContext);
  const navigate = useNavigate();
  const { username } = useParams();
  const currentUser = JSON.parse(localStorage.getItem('user')) || {};
  const userRole = currentUser.role;

  const [previousTheme, setPreviousTheme] = useState(theme);
  const [customColors, setCustomColors] = useState(theme.colors || defaultTheme.colors);
  const [currentTheme, setCurrentTheme] = useState(theme.name || defaultTheme.name);
  const [tempColors, setTempColors] = useState(theme.colors || defaultTheme.colors);
  const [tempTypography, setTempTypography] = useState(theme.typography || defaultTheme.typography);

  // NEW: state for font input + dropdown
  const [fontSearch, setFontSearch] = useState('');
  const [isFontDropdownOpen, setIsFontDropdownOpen] = useState(false);

  // Sync state with context theme changes
  useEffect(() => {
    if (isThemeLoaded) {
      console.log('ThemePage: Syncing with context theme:', theme);
      setCustomColors(theme.colors || defaultTheme.colors);
      setTempColors(theme.colors || defaultTheme.colors);
      setTempTypography(theme.typography || defaultTheme.typography);
      setCurrentTheme(theme.name || defaultTheme.name);
      setPreviousTheme(theme);
      
    }
  }, [theme, isThemeLoaded]);

  // Keep fontSearch in sync with selected font
  useEffect(() => {
    const currentValue = tempTypography.fontFamily || defaultTheme.typography.fontFamily;
    const currentOption = fontOptions.find((opt) => opt.value === currentValue);
    setFontSearch(currentOption ? currentOption.label : '');
  }, [tempTypography.fontFamily]);

  // Function to convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : null;
  };

  // Function to generate lighter and darker shades
  const generateShades = (hex) => {
    const rgb = hexToRgb(hex)
      ?.split(',')
      ?.map((val) => parseInt(val.trim(), 10));
    if (!rgb) return { light: hex, dark: hex };

    const light = `#${Math.min(255, rgb[0] + 40)
      .toString(16)
      .padStart(2, '0')}${Math.min(255, rgb[1] + 40)
      .toString(16)
      .padStart(2, '0')}${Math.min(255, rgb[2] + 40)
      .toString(16)
      .padStart(2, '0')}`;

    const dark = `#${Math.max(0, rgb[0] - 40)
      .toString(16)
      .padStart(2, '0')}${Math.max(0, rgb[1] - 40)
      .toString(16)
      .padStart(2, '0')}${Math.max(0, rgb[2] - 40)
      .toString(16)
      .padStart(2, '0')}`;

    return { light, dark };
  };

  // Predefined themes
  const themes = {
    default: {
      name: '(Default) Ocean Blue',
      colors: {
        primary: '#0ea5e9',
        primaryLight: '#38bdf8',
        primaryDark: '#0284c7',
        primaryRgb: '14, 165, 233',
        secondary: '#06b6d4',
        secondaryLight: '#22d3ee',
        secondaryDark: '#0e7490',
        secondaryRgb: '6, 182, 212',
        success: '#198754',
        successLight: '#75b798',
        successDark: '#146c43',
        successRgb: '25, 135, 84',
        warning: '#ffc107',
        warningLight: '#ffda6a',
        warningDark: '#cc9a06',
        warningRgb: '255, 193, 7',
        danger: '#dc3545',
        dangerLight: '#ea868f',
        dangerDark: '#b02a37',
        dangerRgb: '220, 53, 69',
        errorBorder: '#dc3545',
        errorText: '#dc3545',
        errorBg: '#f8d7da',
        info: '#0dcaf0',
        infoLight: '#6edff6',
        infoDark: '#0aa2c0',
        infoRgb: '13, 202, 240',
        bgPrimary: '#ffffff',
        bgSecondary: '#f8f9fa',
        bgTertiary: '#e9ecef',
        bgDark: '#212529',
        textPrimary: '#212529',
        textSecondary: '#6c757d',
        textMuted: '#adb5bd',
        textWhite: '#ffffff',
        borderColor: '#dee2e6',
        borderLight: '#f8f9fa',
        borderDark: '#adb5bd',
      },
      typography: defaultTheme.typography,
    },
    classic: {
      name: 'Classic Blue',
      colors: {
        primary: '#296ed6',
        primaryLight: '#6ea8fe',
        primaryDark: '#0a58ca',
        primaryRgb: '41, 110, 214',
        secondary: '#6ba4d6',
        secondaryLight: '#adb5bd',
        secondaryDark: '#495057',
        secondaryRgb: '107, 164, 214',
        success: '#198754',
        successLight: '#75b798',
        successDark: '#146c43',
        successRgb: '25, 135, 84',
        warning: '#ffc107',
        warningLight: '#ffda6a',
        warningDark: '#cc9a06',
        warningRgb: '255, 193, 7',
        danger: '#dc3545',
        dangerLight: '#ea868f',
        dangerDark: '#b02a37',
        dangerRgb: '220, 53, 69',
        errorBorder: '#dc3545',
        errorText: '#dc3545',
        errorBg: '#f8d7da',
        info: '#0dcaf0',
        infoLight: '#6edff6',
        infoDark: '#0aa2c0',
        infoRgb: '13, 202, 240',
        bgPrimary: '#ffffff',
        bgSecondary: '#f8f9fa',
        bgTertiary: '#e9ecef',
        bgDark: '#212529',
        textPrimary: '#212529',
        textSecondary: '#6c757d',
        textMuted: '#adb5bd',
        textWhite: '#ffffff',
        borderColor: '#dee2e6',
        borderLight: '#f8f9fa',
        borderDark: '#adb5bd',
      },
      typography: defaultTheme.typography,
    },
    sunset: {
      name: 'Sunset Orange',
      colors: {
        primary: '#f97316',
        primaryLight: '#fb923c',
        primaryDark: '#ea580c',
        primaryRgb: '249, 115, 22',
        secondary: '#fdba74',
        secondaryLight: '#fed7aa',
        secondaryDark: '#f4a261',
        secondaryRgb: '253, 186, 116',
        success: '#84cc16',
        successLight: '#a3e635',
        successDark: '#65a30d',
        successRgb: '132, 204, 22',
        warning: '#fbbf24',
        warningLight: '#fcd34d',
        warningDark: '#f59e0b',
        warningRgb: '251, 191, 36',
        danger: '#f87171',
        dangerLight: '#fca5a5',
        dangerDark: '#ef4444',
        dangerRgb: '248, 113, 113',
        errorBorder: '#f87171',
        errorText: '#f87171',
        errorBg: '#fca5a5',
        info: '#38bdf8',
        infoLight: '#7dd3fc',
        infoDark: '#0ea5e9',
        infoRgb: '56, 189, 248',
        bgPrimary: '#ffffff',
        bgSecondary: '#fff7ed',
        bgTertiary: '#ffedd5',
        bgDark: '#7c2d12',
        textPrimary: '#7c2d12',
        textSecondary: '#9a3412',
        textMuted: '#4b7289',
        textWhite: '#ffffff',
        borderColor: '#fed7aa',
        borderLight: '#ffedd5',
        borderDark: '#4b7289',
      },
      typography: defaultTheme.typography,
    },
    purple: {
      name: 'Royal Purple',
      colors: {
        primary: '#8b5cf6',
        primaryLight: '#a78bfa',
        primaryDark: '#7c3aed',
        primaryRgb: '139, 92, 246',
        secondary: '#c4b5fd',
        secondaryLight: '#ddd6fe',
        secondaryDark: '#a78bfa',
        secondaryRgb: '196, 181, 253',
        success: '#10b981',
        successLight: '#34d399',
        successDark: '#059669',
        successRgb: '16, 185, 129',
        warning: '#f59e0b',
        warningLight: '#fbbf24',
        warningDark: '#d97706',
        warningRgb: '245, 158, 11',
        danger: '#ef4444',
        dangerLight: '#f87171',
        dangerDark: '#dc2626',
        dangerRgb: '239, 68, 68',
        errorBorder: '#ef4444',
        errorText: '#ef4444',
        errorBg: '#f87171',
        info: '#06b6d4',
        infoLight: '#22d3ee',
        infoDark: '#0e7490',
        infoRgb: '6, 182, 212',
        bgPrimary: '#ffffff',
        bgSecondary: '#f5f3ff',
        bgTertiary: '#ede9fe',
        bgDark: '#4c1d95',
        textPrimary: '#4c1d95',
        textSecondary: '#6d28d9',
        textMuted: '#4b7289',
        textWhite: '#ffffff',
        borderColor: '#ddd6fe',
        borderLight: '#ede9fe',
        borderDark: '#4b7289',
      },
      typography: defaultTheme.typography,
    },
    slate: {
      name: 'Slate Elegance',
      colors: {
        primary: '#4b5563',
        primaryLight: generateShades('#4b5563').light,
        primaryDark: generateShades('#4b5563').dark,
        primaryRgb: hexToRgb('#4b5563'),
        secondary: '#60a5fa',
        secondaryLight: generateShades('#60a5fa').light,
        secondaryDark: generateShades('#60a5fa').dark,
        secondaryRgb: hexToRgb('#60a5fa'),
        success: '#198754',
        successLight: '#75b798',
        successDark: '#146c43',
        successRgb: '25, 135, 84',
        warning: '#ffc107',
        warningLight: '#ffda6a',
        warningDark: '#cc9a06',
        warningRgb: '255, 193, 7',
        danger: '#dc3545',
        dangerLight: '#ea868f',
        dangerDark: '#b02a37',
        dangerRgb: '220, 53, 69',
        errorBorder: '#dc3545',
        errorText: '#dc3545',
        errorBg: '#f8d7da',
        info: '#0dcaf0',
        infoLight: '#6edff6',
        infoDark: '#0aa2c0',
        infoRgb: '13, 202, 240',
        bgPrimary: '#ffffff',
        bgSecondary: '#f8f9fa',
        bgTertiary: '#e9ecef',
        bgDark: '#1f2937',
        textPrimary: '#1f2937',
        textSecondary: '#6b7280',
        textMuted: '#9ca3af',
        textWhite: '#ffffff',
        borderColor: '#d1d5db',
        borderLight: '#e5e7eb',
        borderDark: '#6b7280',
      },
      typography: defaultTheme.typography,
    },
    pink: {
      name: 'Soft Pink Bliss',
      colors: {
        primary: '#f9a8d4',
        primaryLight: generateShades('#f9a8d4').light,
        primaryDark: generateShades('#f9a8d4').dark,
        primaryRgb: hexToRgb('#f9a8d4'),
        secondary: '#f9d7ea',
        secondaryLight: generateShades('#f9d7ea').light,
        secondaryDark: generateShades('#f9d7ea').dark,
        secondaryRgb: hexToRgb('#f9d7ea'),
        success: '#198754',
        successLight: '#75b798',
        successDark: '#146c43',
        successRgb: '25, 135, 84',
        warning: '#ffc107',
        warningLight: '#ffda6a',
        warningDark: '#cc9a06',
        warningRgb: '255, 193, 7',
        danger: '#dc3545',
        dangerLight: '#ea868f',
        dangerDark: '#b02a37',
        dangerRgb: '220, 53, 69',
        errorBorder: '#dc3545',
        errorText: '#dc3545',
        errorBg: '#f8d7da',
        info: '#0dcaf0',
        infoLight: '#6edff6',
        infoDark: '#0aa2c0',
        infoRgb: '13, 202, 240',
        bgPrimary: '#ffffff',
        bgSecondary: '#fdf2f8',
        bgTertiary: '#fce7f3',
        bgDark: '#4b3b4f',
        textPrimary: '#4b3b4f',
        textSecondary: '#6b5b6f',
        textMuted: '#9ca3af',
        textWhite: '#ffffff',
        borderColor: '#f3d6e6',
        borderLight: '#fce7f3',
        borderDark: '#9ca3af',
      },
      typography: defaultTheme.typography,
    },
    cotton: {
      name: 'Cotton Candy',
      colors: {
        primary: '#f0abfc',
        primaryLight: generateShades('#f0abfc').light,
        primaryDark: generateShades('#f0abfc').dark,
        primaryRgb: hexToRgb('#f0abfc'),
        secondary: '#c4b5fd',
        secondaryLight: generateShades('#c4b5fd').light,
        secondaryDark: generateShades('#c4b5fd').dark,
        secondaryRgb: hexToRgb('#c4b5fd'),
        success: '#198754',
        successLight: '#75b798',
        successDark: '#146c43',
        successRgb: '25, 135, 84',
        warning: '#ffc107',
        warningLight: '#ffda6a',
        warningDark: '#cc9a06',
        warningRgb: '255, 193, 7',
        danger: '#dc3545',
        dangerLight: '#ea868f',
        dangerDark: '#b02a37',
        dangerRgb: '220, 53, 69',
        errorBorder: '#dc3545',
        errorText: '#dc3545',
        errorBg: '#f8d7da',
        info: '#0dcaf0',
        infoLight: '#6edff6',
        infoDark: '#0aa2c0',
        infoRgb: '13, 202, 240',
        bgPrimary: '#ffffff',
        bgSecondary: '#faf5ff',
        bgTertiary: '#f3e8ff',
        bgDark: '#4c1d95',
        textPrimary: '#4c1d95',
        textSecondary: '#6d28d9',
        textMuted: '#9ca3af',
        textWhite: '#ffffff',
        borderColor: '#e9d5ff',
        borderLight: '#f3e8ff',
        borderDark: '#9ca3af',
      },
      typography: defaultTheme.typography,
    },
  };

  // Handle predefined theme selection
  const handleThemeChange = (themeName) => {
    const selectedTheme = themes[themeName];
    if (selectedTheme) {
      console.log('Selecting predefined theme:', themeName);
      setTempColors(selectedTheme.colors);
      setTempTypography(selectedTheme.typography || defaultTheme.typography);
      setCurrentTheme(themeName);
    }
  };

  // Handle custom color change
  const handleCustomColorChange = (colorKey, value) => {
    console.log('Changing color:', colorKey, value);
    const shades = generateShades(value);
    const rgb = hexToRgb(value);
    const newColors = {
      ...tempColors,
      [colorKey]: value,
      [`${colorKey}Light`]: shades.light,
      [`${colorKey}Dark`]: shades.dark,
      [`${colorKey}Rgb`]: rgb,
    };
    setTempColors(newColors);
    setCurrentTheme('custom');
  };

  // Handle font family change
  const handleFontFamilyChange = (value) => {
    console.log('Changing font family:', value);
    setTempTypography((prev) => ({
      ...prev,
      fontFamily: value,
    }));
    setCurrentTheme('custom');
  };

  // Handle font size change (title/body)
  const handleFontSizeChange = (key, value) => {
    const numericValue = parseInt(value, 10) || 0;
    console.log('Changing font size:', key, numericValue);
    setTempTypography((prev) => ({
      ...prev,
      [key]: numericValue,
    }));
    setCurrentTheme('custom');
  };

  // Handle save with retry logic
  const handleSave = async () => {
  const newTheme = {
    name: currentTheme,
    colors: { ...tempColors },
    typography: { ...tempTypography },
  };

  console.log('Saving theme:', newTheme);

  try {
    const response = await updateTheme({ theme: newTheme });
    const savedTheme = response?.data?.theme || newTheme;

    setTempColors(savedTheme.colors || defaultTheme.colors);
    setTempTypography(savedTheme.typography || defaultTheme.typography);
    setCurrentTheme(savedTheme.name || defaultTheme.name);
    setCustomColors(savedTheme.colors || defaultTheme.colors);
    setTheme(savedTheme);
    setPreviousTheme(savedTheme);

    toast.success('Theme updated successfully!', {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastStyle: {
        backgroundColor: tempColors.bgPrimary || defaultTheme.colors.bgPrimary,
        border: `1px solid ${tempColors.borderColor || defaultTheme.colors.borderColor}`,
        color: tempColors.textPrimary || defaultTheme.colors.textPrimary,
        fontFamily: tempTypography.fontFamily || defaultTheme.typography.fontFamily,
        fontSize: `${tempTypography.bodyFontSize || defaultTheme.typography.bodyFontSize}px`,
      },
    });

    if (userRole === 'client_admin') {
      setTimeout(() => {
        navigate(`/${username}/dashboard/account`, { replace: true });
      }, 1000);
    } else {
      setTimeout(() => {
        navigate(`/${username}/account`, { replace: true });
      }, 1000);
    }
  } catch (err) {
    console.error('Error saving theme:', err.response?.data?.message || err.message);
    toast.error('Failed to save theme. Please try again.', {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastStyle: {
        backgroundColor: tempColors.errorBg || defaultTheme.colors.errorBg,
        border: `1px solid ${tempColors.errorBorder || defaultTheme.colors.errorBorder}`,
        color: tempColors.errorText || defaultTheme.colors.errorText,
        fontFamily: tempTypography.fontFamily || defaultTheme.typography.fontFamily,
        fontSize: `${tempTypography.bodyFontSize || defaultTheme.typography.bodyFontSize}px`,
      },
    });
  }
};

  // Handle cancel
  const handleCancel = () => {
    console.log('Cancelling theme changes, restoring:', previousTheme);
    setTempColors(previousTheme.colors || defaultTheme.colors);
    setTempTypography(previousTheme.typography || defaultTheme.typography);
    setCurrentTheme(previousTheme.name || defaultTheme.name);
    if (userRole === 'client_admin') {
      navigate(`/${username}/dashboard/account`, { replace: true });
    } else {
      navigate(`/${username}/account`, { replace: true });
    }
  };

  // Handle reset to default theme
  const handleReset = async () => {
    const defaultThemeConfig = {
      name: defaultTheme.name,
      colors: { ...defaultTheme.colors },
      typography: { ...defaultTheme.typography },
    };
    console.log('Resetting to default theme:', defaultThemeConfig);
    try {
      const response = await updateTheme({ theme: defaultThemeConfig });
const savedTheme = response?.data?.theme || defaultThemeConfig;

setTempColors(savedTheme.colors || defaultTheme.colors);
setTempTypography(savedTheme.typography || defaultTheme.typography);
setCurrentTheme(savedTheme.name || defaultTheme.name);
setCustomColors(savedTheme.colors || defaultTheme.colors);
setTheme(savedTheme);
setPreviousTheme(savedTheme);
      toast.success('Theme reset to default!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastStyle: {
          backgroundColor: defaultTheme.colors.bgPrimary,
          border: `1px solid ${defaultTheme.colors.borderColor}`,
          color: defaultTheme.colors.textPrimary,
          fontFamily: defaultTheme.typography.fontFamily,
          fontSize: `${defaultTheme.typography.bodyFontSize}px`,
        },
      });
    } catch (err) {
      console.error('Error resetting theme:', err.response?.data?.message || err.message);
      toast.error('Failed to reset theme. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastStyle: {
          backgroundColor: defaultTheme.colors.errorBg,
          border: `1px solid ${defaultTheme.colors.errorBorder}`,
          color: defaultTheme.colors.errorText,
          fontFamily: defaultTheme.typography.fontFamily,
          fontSize: `${defaultTheme.typography.bodyFontSize}px`,
        },
      });
    }
  };

  // Apply theme styles for preview only (same look as your original)
  useEffect(() => {
    console.log('Applying preview styles with colors and typography:', tempColors, tempTypography);
    const styleSheet = document.createElement('style');
    document.head.appendChild(styleSheet);

    const fontFamily = tempTypography.fontFamily || defaultTheme.typography.fontFamily;
    const titleFontSize = `${tempTypography.titleFontSize || defaultTheme.typography.titleFontSize}px`;
    const bodyFontSize = `${tempTypography.bodyFontSize || defaultTheme.typography.bodyFontSize}px`;

    const styles = `
      .theme-manager { 
        width: 100%;
        min-height: calc(10vh - 0.5px);
        padding: clamp(0.125rem, 1vw, 0.25rem);
        box-sizing: border-box;
        font-family: ${fontFamily};
        font-size: ${bodyFontSize};
      }
      .theme-container {
        width: 100%;
        padding: clamp(0.125rem, 1vw, 0.25rem);
        box-sizing: border-box;
      }
      .theme-card { 
        background-color: ${tempColors.bgPrimary || defaultTheme.colors.bgPrimary}; 
        border: none; 
        width: 100%; 
        padding: 16px;
        box-sizing: border-box;
        margin-bottom: 0.5rem;
        font-family: ${fontFamily};
        font-size: ${bodyFontSize};
      }
      .theme-card h3,
      .theme-card h4,
      .theme-card h5,
      .theme-card h6,
      .theme-title { 
        color: ${tempColors.textPrimary || defaultTheme.colors.textPrimary};
        font-size: ${titleFontSize};
        font-weight: 600;
      }
      .text-primary { 
        color: ${tempColors.textPrimary || defaultTheme.colors.textPrimary} !important; 
      }
      .btn-primary { 
        background: ${tempColors.primary || defaultTheme.colors.primary}; 
        border: none; 
        color: ${tempColors.textWhite || defaultTheme.colors.textWhite};
        font-family: ${fontFamily};
      }
      .btn-primary:hover { 
        background: ${tempColors.primaryDark || defaultTheme.colors.primaryDark}; 
      }
      .btn-secondary { 
        background: ${tempColors.secondary || defaultTheme.colors.secondary}; 
        border: none; 
        color: ${tempColors.textWhite || defaultTheme.colors.textWhite}; 
        font-family: ${fontFamily};
      }
      .btn-secondary:hover { 
        background: ${tempColors.secondaryDark || defaultTheme.colors.secondaryDark}; 
      }
      .btn-outline-primary { 
        color: ${tempColors.primary || defaultTheme.colors.primary}; 
        border-color: ${tempColors.primary || defaultTheme.colors.primary}; 
        font-family: ${fontFamily};
      }
      .btn-outline-primary:hover, .btn-outline-primary:focus { 
        background: ${tempColors.primaryLight || defaultTheme.colors.primaryLight};
        border-color: ${tempColors.borderColor || defaultTheme.colors.borderColor};
        outline: none;
      }
      .theme-btn-outline { 
        border-color: ${tempColors.borderColor || defaultTheme.colors.borderColor}; 
        color: ${tempColors.textPrimary || defaultTheme.colors.textPrimary}; 
        font-family: ${fontFamily};
      }
      .theme-btn-outline:hover { 
        background: ${tempColors.bgTertiary || defaultTheme.colors.bgTertiary}; 
      }
      .theme-form-control { 
        background-color: ${tempColors.bgPrimary || defaultTheme.colors.bgPrimary}; 
        color: ${tempColors.textPrimary || defaultTheme.colors.textPrimary}; 
        border-color: ${tempColors.borderColor || defaultTheme.colors.borderColor}; 
        outline: none; 
        box-shadow: none; 
        font-family: ${fontFamily};
        font-size: ${bodyFontSize};
      }
      .form-control-color { 
        outline: none; 
        box-shadow: none; 
      }
    `;
    styleSheet.textContent = styles;
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, [tempColors, tempTypography]);

  if (!isThemeLoaded) {
    console.log('ThemePage: Waiting for theme to load');
    return <div>Loading theme...</div>;
  }

  // Filtered list for font dropdown
  const filteredFontOptions = fontOptions.filter((opt) =>
    opt.label.toLowerCase().includes(fontSearch.toLowerCase())
  );

  console.log('ThemePage: Rendering with theme:', theme);
  return (
    <div className="theme-manager">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div className="theme-container">
        <div className="theme-card">
          <h3 className="theme-title mb-3">
            <i className="bi bi-palette me-2"></i>
            Theme Manager
          </h3>

          {/* Predefined Themes */}
          <div className="mb-3">
            <h5 className="mb-2">Quick Themes</h5>
            <div className="row g-2">
              {Object.entries(themes).map(([key, themeItem]) => (
                <div key={key} className="col-md-4 col-sm-6">
                  <button
                    className={`btn w-100 d-flex align-items-center justify-content-between ${
                      currentTheme === key ? 'btn-primary' : 'theme-btn-outline'
                    }`}
                    onClick={() => handleThemeChange(key)}
                  >
                    <span>{themeItem.name}</span>
                    <div className="d-flex">
                      {[themeItem.colors.primary, themeItem.colors.secondary, themeItem.colors.borderLight].map(
                        (color, index) => (
                          <div
                            key={index}
                            className="rounded-circle ms-1"
                            style={{
                              width: '12px',
                              height: '12px',
                              backgroundColor: color,
                              border: '1px solid rgba(0,0,0,0.1)',
                            }}
                          />
                        )
                      )}
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Colors */}
          <div className="mb-3">
            <h5 className="mb-2">Custom Colors</h5>
            <div className="row g-2">
              {['primary', 'secondary', 'warning', 'info'].map((key) => (
                <div key={key} className="col-md-6 col-lg-4">
                  <div className="theme-card p-2">
                    <label className="form-label text-capitalize fw-bold">{key} Color</label>
                    <div className="d-flex align-items-center">
                      <input
                        type="color"
                        className="form-control form-control-color me-2"
                        value={tempColors[key] || '#000000'}
                        onChange={(e) => handleCustomColorChange(key, e.target.value)}
                        style={{ width: '50px', height: '38px', outline: 'none', boxshadow: 'none' }}
                      />
                      <input
                        type="text"
                        className="form-control theme-form-control"
                        value={tempColors[key] || '#000000'}
                        onChange={(e) => handleCustomColorChange(key, e.target.value)}
                        placeholder="#000000"
                        style={{ width: '100%', outline: 'none', boxshadow: 'none' }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Typography Settings (3 equal columns like quick themes) */}
          <div className="mb-3">
            <h5 className="mb-2">Font Style</h5>
            <div className="row g-2">
              {/* FONT INPUT + DROPDOWN */}
              <div className="col-md-4 col-sm-6">
                <div className="theme-card p-2 position-relative">
                  <label className="form-label fw-bold">Font</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control theme-form-control"
                      placeholder="Search font..."
                      value={fontSearch}
                      onChange={(e) => {
                        setFontSearch(e.target.value);
                        setIsFontDropdownOpen(true);
                      }}
                      onFocus={() => setIsFontDropdownOpen(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const first = filteredFontOptions[0];
                          if (first) {
                            handleFontFamilyChange(first.value);
                            setFontSearch(first.label);
                            setIsFontDropdownOpen(false);
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setIsFontDropdownOpen((prev) => !prev)}
                      style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                    >
                      <i className="bi bi-caret-down-fill" />
                    </button>
                  </div>

                  {isFontDropdownOpen && (
                    <div
                      className="bg-white border rounded shadow-sm position-absolute w-100"
                      style={{
                        zIndex: 10,
                        maxHeight: '220px', // ~8 items
                        overflowY: 'auto',
                        marginTop: '4px',
                      }}
                    >
                      {filteredFontOptions.length > 0 ? (
                        filteredFontOptions.map((opt) => (
                          <div
                            key={opt.value}
                            onClick={() => {
                              handleFontFamilyChange(opt.value);
                              setFontSearch(opt.label);
                              setIsFontDropdownOpen(false);
                            }}
                            style={{
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontFamily: opt.value,
                            }}
                            className="font-option-item"
                          >
                            {opt.label}
                          </div>
                        ))
                      ) : (
                        <div
                          className="text-muted"
                          style={{ padding: '4px 8px' }}
                        >
                          No fonts found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Title Font Size */}
              <div className="col-md-4 col-sm-6">
                <div className="theme-card p-2">
                  <label className="form-label fw-bold">Title Font Size</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control theme-form-control"
                      min={8}
                      max={72}
                      value={tempTypography.titleFontSize || defaultTheme.typography.titleFontSize}
                      onChange={(e) => handleFontSizeChange('titleFontSize', e.target.value)}
                    />
                    <span className="input-group-text">px</span>
                  </div>
                  <small className="text-muted">Used for titles/headings</small>
                </div>
              </div>

              {/* Body Font Size */}
              <div className="col-md-4 col-sm-6">
                <div className="theme-card p-2">
                  <label className="form-label fw-bold">Body Font Size</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control theme-form-control"
                      min={8}
                      max={72}
                      value={tempTypography.bodyFontSize || defaultTheme.typography.bodyFontSize}
                      onChange={(e) => handleFontSizeChange('bodyFontSize', e.target.value)}
                    />
                    <span className="input-group-text">px</span>
                  </div>
                  <small className="text-muted">Used for content below titles</small>
                </div>
              </div>
            </div>
          </div>

          {/* Theme Preview */}
          <div className="mb-3">
            <h5 className="mb-2">Theme Preview</h5>
            <div className="row g-2">
              <div className="col-12">
                {/* Removed extra container styling, just plain theme-card */}
                <div className="theme-card p-3">
                  <div className="row align-items-center g-3">
                    {/* Left column: Title + Body preview */}
                    <div className="col-md-4">
                      <div
                        style={{
                          fontSize: `${tempTypography.titleFontSize || defaultTheme.typography.titleFontSize}px`,
                          fontWeight: 600,
                          marginBottom: '4px',
                        }}
                      >
                        Title Preview
                      </div>
                      <div
                        style={{
                          fontSize: `${tempTypography.bodyFontSize || defaultTheme.typography.bodyFontSize}px`,
                        }}
                      >
                        Body text preview using the selected font style.
                      </div>
                    </div>

                    {/* Center column: Buttons title + buttons, centered */}
                    <div className="col-md-4 text-center">
                      <div
                        style={{
                          fontWeight: 600,
                          marginBottom: '6px',
                        }}
                      >
                        Buttons
                      </div>
                      <div className="d-flex flex-wrap justify-content-center gap-2">
                        <button className="btn btn-primary">Primary</button>
                        <button className="btn btn-secondary">Secondary</button>
                        <button className="btn btn-outline-primary">Outline</button>
                      </div>
                    </div>

                    {/* Right column left empty to keep layout balanced */}
                    <div className="col-md-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Save, Cancel, and Reset Buttons */}
          <div className="d-flex gap-2 justify-content-end flex-wrap">
            <button className="btn btn-primary" onClick={handleSave} style={{ minWidth: '120px' }}>
              Save
            </button>
            <button className="btn btn-secondary" onClick={handleCancel} style={{ minWidth: '120px' }}>
              Cancel
            </button>
            <button className="btn btn-outline-primary" onClick={handleReset} style={{ minWidth: '120px' }}>
              Reset to Default
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemePage;
